import {
	Box,
	Button,
	FormControl,
	InputLabel,
	NativeSelect,
	TextField,
	Typography,
	useTheme,
} from "@mui/material"
import useMediaQuery from "@mui/material/useMediaQuery"
import * as yup from "yup"

import { EditNotificationsOutlined } from "@mui/icons-material"
import { DataGrid, GridDeleteIcon } from "@mui/x-data-grid"
import { useFormik } from "formik"
import moment from "moment/moment"
import { useEffect, useState } from "react"
import Header from "../../components/Header"
import UserRoleApi from "../../services/api/UserRole"
import RoleApi from "../../services/api/roles"
import UsersApi from "../../services/api/users"
import { tokens } from "../../theme"
import ConfirmationDialog from "../../components/ConfirmationDialog"

const UserRole = () => {
	const isNonMobile = useMediaQuery("(min-width:600px)")
	const theme = useTheme()
	const colors = tokens(theme.palette.mode)
	const [isEdit, setIsEdit] = useState(false)
	const [users, setUsers] = useState([])
	const [roles, setRoles] = useState([])
	const [userId, setUserId] = useState()
	const [userRoles, setUserRoles] = useState([])

	const columns = [
		{
			field: "roleName",
			headerName: "Role",
			flex: 1,
			cellClassName: "name-column--cell",
		},
		{
			field: "expireDate",
			headerName: "Expire Date",
			valueFormatter: (params) => {
				return moment(params.value).format("DD-MM-YYYY")
			},
			flex: 1,
			cellClassName: "name-column--cell",
		},
		{
			field: "",
			headerName: "Action",
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Box display="flex" justifyContent="center" borderRadius="4px">
						<Typography
							variant="button"
							color="warning"
							onClick={() => edit(row)}
							style={{ cursor: "pointer", marginRight: "10px" }}
						>
							<EditNotificationsOutlined
								fontSize="small"
								style={{ marginRight: "4px" }}
							/>
							Edit
						</Typography>

						<Typography
							variant="button"
							color="error"
							onClick={() => destroy(row)}
							style={{ cursor: "pointer" }}
						>
							<GridDeleteIcon fontSize="small" style={{ marginRight: "4px" }} />
							Delete
						</Typography>
					</Box>
				)
			},
		},
	]

	useEffect(() => {
		fetchUsers()
		fetchRoles()
	}, [])

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: checkoutSchema,

		onSubmit: async (values, { resetForm }) => {
			if (isEdit) {
				updateEntity(values)
			} else {
				createEntity(values)
			}

			resetForm({ values: { ...initialValues, user_id: userId } })
			handleAfterReset()
		},
	})

	const createEntity = async (values) => {
		const response = await UserRoleApi().create(values)

		console.log(createdSuccessMsg, response)
		fetchUserRoles(userId)
	}

	const updateEntity = async (values) => {
		const response = await UserRoleApi().edit(values.id, values)

		console.log(updatedSuccessMsg, response)
		fetchUserRoles(userId)
	}

	const handleAfterReset = () => {
		setIsEdit(false)
	}

	const fetchUsers = async () => {
		try {
			const data = await UsersApi().getAllUsers()

			setUsers(data.users)
		} catch (error) {
			console.log("Not loading users", error)
		}
	}

	const fetchRoles = async () => {
		try {
			const data = await RoleApi().getAll()

			setRoles(data.results)
		} catch (error) {
			console.log("Not loading users", error)
		}
	}

	const handleUserChange = (event) => {
		setUserId(event.target.value)
		fetchUserRoles(event.target.value)
		formik.handleChange(event)
	}

	const edit = (row) => {
		if (row) {
			formik.setValues({
				id: row.id,
				user_id: row.userId,
				role_id: row.roleId,
				expire_date: moment(row.expireDate).format("YYYY-MM-DD"),
			})

			setIsEdit(true)
		}
	}

	const [isConfirmationOpen, setConfirmationOpen] = useState(false)
	const [selectedId, setSelectedId] = useState("")

	const destroy = (row) => {
		setSelectedId(row.id)
		handleOpenConfirmation()
	}

	const handleOpenConfirmation = () => {
		setConfirmationOpen(true)
	}

	const handleCloseConfirmation = () => {
		setConfirmationOpen(false)
	}

	const handleConfirmAction = () => {
		try {
			const response = UserRoleApi().destroy(selectedId)

			console.log(deletedSuccessMsg, response)
			fetchUserRoles(userId)
		} catch (error) {
			console.log(deleteErrorMsg, error)
		}

		handleCloseConfirmation()
	}

	const fetchUserRoles = async (userId) => {
		try {
			const data = await UserRoleApi().getByUserId(userId)

			setUserRoles(data)
		} catch (error) {
			setUserRoles([])
			if (error.response.status === 404) {
				console.log("This user has no roles.")
			} else {
				console.log("Loading error", error.response)
			}
		}
	}

	return (
		<>
			<Box m="20px">
				<Header title="User Role Assignment" subtitle="" />
				<form onSubmit={formik.handleSubmit}>
					<Box
						display="grid"
						gap="30px"
						gridTemplateColumns="repeat(4, minmax(0, 1fr))"
						sx={{
							"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
						}}
					>
						<Box sx={{ gridColumn: "span 2" }}>
							<FormControl fullWidth>
								<InputLabel variant="standard" htmlFor="uncontrolled-native">
									User
								</InputLabel>
								<NativeSelect
									value={formik.values.user_id}
									onChange={handleUserChange}
									inputProps={{
										name: "user_id",
									}}
								>
									<option value=""></option>
									{users
										? users.map((user) => (
												<option value={user.id}>{user.name}</option>
										  ))
										: null}
								</NativeSelect>
							</FormControl>

							<Box
								m="20px 0 0 0"
								display="grid"
								gap="30px"
								gridTemplateColumns="repeat(4, minmax(0, 1fr))"
								sx={{
									"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
								}}
							>
								<FormControl
									fullWidth
									sx={{ gridColumn: "span 2", marginTop: "8px" }}
								>
									<InputLabel variant="standard" htmlFor="uncontrolled-native">
										Role
									</InputLabel>
									<NativeSelect
										value={formik.values.role_id}
										onChange={formik.handleChange}
										inputProps={{
											name: "role_id",
										}}
									>
										<option value=""></option>
										{roles
											? roles.map((role) => (
													<option value={role.id}>{role.name}</option>
											  ))
											: null}
									</NativeSelect>
								</FormControl>

								<TextField
									fullWidth
									variant="filled"
									type="date"
									label="Expire Date"
									onBlur={formik.handleBlur}
									onChange={formik.handleChange}
									name="expire_date"
									value={formik.values.expire_date}
									error={
										!!formik.touched.expire_date && !!formik.errors.expire_date
									}
									helperText={
										formik.touched.expire_date && formik.errors.expire_date
									}
									sx={{ gridColumn: "span 2" }}
								/>
							</Box>
							<Box display="flex" justifyContent="end" mt="20px">
								<Button type="submit" color="secondary" variant="contained">
									{isEdit ? "Update" : "Save"}
								</Button>
							</Box>
						</Box>

						<Box sx={{ gridColumn: "span 2" }}>
							<Box
								height="75vh"
								sx={{
									"& .MuiDataGrid-root": {
										border: "none",
									},
									"& .MuiDataGrid-cell": {
										borderBottom: "none",
									},
									"& .name-column--cell": {
										color: colors.greenAccent[300],
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: colors.blueAccent[700],
										borderBottom: "none",
									},
									"& .MuiDataGrid-virtualScroller": {
										backgroundColor: colors.primary[400],
									},
									"& .MuiDataGrid-footerContainer": {
										borderTop: "none",
										backgroundColor: colors.blueAccent[700],
									},
									"& .MuiCheckbox-root": {
										color: `${colors.greenAccent[200]} !important`,
									},
								}}
							>
								{userRoles && <DataGrid rows={userRoles} columns={columns} />}
							</Box>
						</Box>
					</Box>
				</form>
			</Box>
			<ConfirmationDialog
				open={isConfirmationOpen}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
			/>
		</>
	)
}

const checkoutSchema = yup.object().shape({
	expire_date: yup.date().required("required"),
	role_id: yup.number().required("required"),
	user_id: yup.number().required("required"),
})

const initialValues = {
	user_id: "",
	role_id: "",
	expire_date: moment().format("YYYY-MM-DD"),
}

const formName = "User Role"
const createdSuccessMsg = `${formName} created:`
const updatedSuccessMsg = `${formName} updated:`
const deletedSuccessMsg = `${formName} deleted successfully: `
const deleteErrorMsg = `Not deleted ${formName}`

export default UserRole
